import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import {
  ArrowRightIcon,
  Button,
  EditIcon,
  InputSearch,
  Pagination,
  Select,
  Skeleton,
  TableColumns,
  TableColumnsColumnProps,
  Text,
} from '@mercai/clever'

import { DEFAULT_LIMIT_PAGE } from '@/constants'
import { useParamsState } from '@/helpers'
import { useTransportCompanyList } from '@/services'

import { EmptyState } from '@/components'

import { DrawerLogisticPartnerForm } from '../drawer-logistic-partner-form'

import { TransportCompanyProps } from '@/types'
import { TableLogisticPartnersProps } from './table-logistic-partners.type'

import styles from './table-logistic-partners.module.css'

export const TableLogisticPartners = ({
  externalFilter,
  setExternalFilter,
}: TableLogisticPartnersProps) => {
  const { t } = useTranslation('screenLogistic')

  const [totalPages, setTotalPages] = useState(1)

  const [queryParams, setQueryParams] = useParamsState({
    uuid: '',
    show: '',
    form: '',
    tab: '',
  })

  const [filter, setFilter] = useParamsState({
    ...externalFilter,
    page: '1',
    limit: `${DEFAULT_LIMIT_PAGE}`,
    search: '',
    logisticRoute_Uuid: 'all',
  })

  const navigate = useNavigate()

  const handleFilter = (changedKey: string, newValue: string) => {
    setFilter({
      ...filter,
      ...(changedKey === 'search' ? { page: '1' } : {}),
      [changedKey]: newValue,
    })
    setExternalFilter?.({
      ...externalFilter,
      ...(changedKey === 'search' ? { page: '1' } : {}),
      [changedKey]: newValue,
    })
  }

  const { data, isLoading } = useTransportCompanyList({
    params: { isActive: true, ...filter },
  })

  useEffect(() => {
    if (data?.data?.count) {
      setTotalPages(Math.ceil(data?.data?.count / +filter.limit))
    }
  }, [data])

  const onHandleEditRegister = (uuid: string) => {
    setQueryParams({ ...queryParams, form: 'true', show: '', uuid })
  }

  const onHandleView = (uuid: string) => {
    navigate(uuid)
  }

  const columns: TableColumnsColumnProps<TransportCompanyProps>[] = [
    {
      title: t('transportCompaniesTableName'),
      key: 'name',
      dataKey: 'name',
    },
    {
      title: t('transportCompaniesTablePhone'),
      key: 'phone',
      dataKey: 'phone',
      width: '12rem',
    },
    {
      title: t('transportCompaniesTableEmail'),
      key: 'email',
      dataKey: 'email',
      width: '21rem',
    },
    {
      title: t('transportCompaniesTableRoute'),
      key: 'routes',
      dataKey: 'transportCompanyRoutes',
      width: '10rem',
      render: ({ record }) =>
        record.transportCompanyRoutes?.filter(
          (transportCompany) => transportCompany?.isActive,
        )?.length || 0,
    },
    {
      title: t('transportCompaniesTableActions'),
      key: 'uuid',
      dataKey: 'uuid',
      width: '8rem',
      render: ({ record }) => (
        <div className={styles['action-buttons']}>
          <Button
            variant="secondary"
            onClick={() => onHandleEditRegister(record.uuid)}
            model="square"
          >
            <EditIcon />
          </Button>
          <Button
            variant="primary"
            onClick={() => onHandleView(record.uuid)}
            model="square"
          >
            <ArrowRightIcon />
          </Button>
        </div>
      ),
      renderLoading: (
        <div className={styles['action-buttons']}>
          <Skeleton height="2.8rem" width="2.8rem" />
          <Skeleton height="2.8rem" width="2.8rem" />
        </div>
      ),
    },
  ]

  return (
    <div className={styles.container}>
      <div className={styles.content}>
        <div>
          <div className={styles.filter}>
            <div className={styles['filter-group']}>
              <InputSearch
                value={filter.search as string}
                onChange={(value) => handleFilter('search', value)}
                className={styles['search-input']}
                translations={{
                  inputPlaceholder: t('searchFilterPlaceholder'),
                }}
              />
            </div>
          </div>

          <TableColumns<TransportCompanyProps>
            isLoading={isLoading}
            columns={columns}
            classNames={{
              expandedContent: styles['expanded-content'],
            }}
            data={data?.data?.results || []}
          />

          {!isLoading && data?.data?.results?.length === 0 && (
            <EmptyState title={t('tableEmptyTitle')} description={''} />
          )}

          <div className={styles.pagination}>
            <div className={styles['pagination-show-registers']}>
              <Text>
                {t('tableShowRegisters', {
                  showNumber:
                    (data?.data?.count || 0) < +filter.limit
                      ? data?.data?.count
                      : +filter.limit,
                  totalNumber: data?.data?.count || 0,
                })}
              </Text>

              <Select
                value={filter.limit}
                onChange={(value) => handleFilter('limit', value)}
                options={[
                  { label: '10', value: '10' },
                  { label: '20', value: '20' },
                  { label: '50', value: '50' },
                  { label: '100', value: '100' },
                ]}
              />
            </div>

            <Pagination
              totalPages={totalPages}
              onChange={(page) => handleFilter('page', `${page}`)}
              currentPage={+filter.page as number}
            />
          </div>
        </div>
      </div>

      <DrawerLogisticPartnerForm
        isOpen={!!queryParams.form}
        onHandleOpen={() =>
          setQueryParams({ ...queryParams, form: '', uuid: '' })
        }
        transportCompany={data?.data?.results?.find(
          ({ uuid }) => uuid === queryParams.uuid,
        )}
        isImporter={queryParams.tab === 'importers'}
      />
    </div>
  )
}
